<template>
  <div>
    <!-- Search -->
    <div class="row mt-4">
      <div class="col-md-6">
        <input
          class="input form-control"
          placeholder="Pesquisar por Placa, Renavam ou CNPJ"
          @keypress.enter="find()"
          v-model="query.generic"
        >
        <search-icon @click="find()" class="icon icon-within"/>
      </div>

      <div class="col-md-6">
        <button
          class="btn btn-outline px-3 mr-2"
          :class="{ 'btn-outline-active': showFilter }"
          @click="showFilter = !showFilter"
        >
          <span>Filtros</span>
          <chevron-down-icon
            class="fa-angle-right"
            :class="{'rotate-90': showFilter }"
          />
        </button>

        <button v-if="changeQuery" @click="find()" class="btn btn-link">
          Aplicar filtros ({{ numberOfBadges }})
        </button>
      </div>
    </div>

    <!-- Badges -->
    <div class="row">
      <div class="col-md-12">
        <BadgeFilters
          :filters="badgesFilters"
          :query="query"
          :amountChanged="updateNumberOfBadges"
          @clearAllBadges="$nextTick(() => find())"
          @clearBadge="$nextTick(() => find())"
        />
      </div>
    </div>

    <!-- Filter -->
    <template v-if="showFilter">
      <div class="row mt-3 mb-5">
        <div class="col-md-6">
          <label>Órgão autuador</label>
          <Select2 :options="filterServiceNames" v-model="query.scrapingServiceName"/>
        </div>

        <div class="col-md-6">
          <label>Status</label>
          <Select2 :options="filterStatus" v-model="query.status"/>
        </div>
      </div>

      <div class="row mt-3 mb-5">
        <div class="col-md-4">
          <label for="type-period">Tipo do período</label>
          <select v-model="query.periodType" id="type-period" class="browser-default custom-select">
            <option value="updated_at">Data de atualização</option>
            <option value="created_at">Data de criação</option>
          </select>
        </div>

        <div class="col-md-4">
          <label for="start-date">Data inicial</label>
          <input v-model="query.startDate" type="date" id="start-date" class="form-control">
        </div>

        <div class="col-md-4">
          <label for="end-date">Data final</label>
          <input v-model="query.endDate" type="date" id="end-date" class="form-control">
        </div>
      </div>
    </template>

    <hr>

    <!-- Register infos -->
    <div class="row mt-3">
      <div class="col-md-12">
        <h5><strong>{{ infoTotalModels }}</strong></h5>
      </div>
    </div>

    <!-- Table -->
    <div class="row mt-3">
      <div class="col-md-12">

        <table class="table table-borderless table-border table-align">
          <thead>
            <tr>
              <th>Serviço</th>
              <th>Placa</th>
              <th>Renavam</th>
              <th>CNPJ</th>
              <th>Status</th>
              <th>Descrição
              </th>
              <th>Criado por</th>
              <th>Criado em</th>
              <th>Atualizado em</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="consult in consults" :key="consult.id">
              <td>{{ getServiceFriendlyName(consult.scrapingServiceName) }}</td>
              <td>{{ consult.plate }}</td>
              <td>{{ consult.renavam }}</td>
              <td>{{ consult.cnpj | cnpj }}</td>
              <td>{{ translateStatus(consult.status) }}</td>
              <td>
                <span v-if="consult.status === 'processed_with_user_error' && consult.errorMessages && consult.errorMessages.length > 0">
                  {{ consult.errorMessages[consult.errorMessages.length - 1].message }}
                </span>
                <span v-else-if="consult.status === 'processed'">
                  Consulta processada com sucesso
                </span>
                <span v-else-if="consult.status === 'error'">
                  Consulta em processamento
                </span>
                <span v-else></span>
              </td>
              <td>{{ consult.createdBy }}</td>
              <td>{{ consult.createdAt | datetime }}</td>
              <td>{{ consult.updatedAt | datetime }}</td>
            </tr>
          </tbody>
        </table>
        
        <div class="row">
          <div class="col-md-12 my-1">
            <Pagination
              v-model="meta.currentPage"
              :total-count="meta.totalCount"
              :total-pages="meta.totalPages"
              @pagination="pagination"
            />
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
  import BadgeFilters from '@/components/shares/BadgeFilters'
  import EntityProxy from '@/models/EntityProxy'
  import ModelMapper from '@/mixins/ModelMapper'
  import handleSearchable from '@/models/handleSearchable'
  import Pagination from '@/components/shares/specialTag/Pagination'
  import Select2 from '../shares/Select2.vue'

  import { ChevronDownIcon, SearchIcon } from 'vue-feather-icons'
  import { Filter } from '@/models/filter'

  export default {
    components: { BadgeFilters, ChevronDownIcon, Pagination, SearchIcon, Select2 },
    computed: {
      badgesFilters() {
        return [
          this.filterServiceNames,
          this.filterEndDate,
          this.filterStartDate,
          this.filterStatus,
          this.filterPeriodType
        ]
      },
      consults() {
        return this.getModels || []
      }
    },
    created() {
      this.$set(this.query, 'periodType', 'created_at')
      this.$set(this.query, 'appName', 'cm-taxas')

      this.order.createdAt = 'desc'
      this.defaultModelSetting.updateQueryURL = true
      this.asyncTemplateSelect({
        byURL: true,
        pagination: true
      })

      this.fetchScrapingServices()
    },
    data: () => ({
      filterEndDate: new Filter({
        key: 'endDate',
        label: 'Data final',
        type: 'date'
      }),
      filterServiceNames: null,
      filterStartDate: new Filter({
        key: 'startDate',
        label: 'Data inicial',
        type: 'date'
      }),
      filterStatus: new Filter({
        key: 'status',
        label: 'Status',
        table: [
          ['Processado', 'processed'],
          ['Em processo', 'processing'],
          ['Erro do usuário', 'processed_with_user_error']
        ]
      }),
      modelPathPrefix: 'maestro',
      nameModel: 'Consult',
      scrapingServices: [],
      showDetailModal: true
    }),
    methods: {
      buildFilterServiceNames(scrapingServices) {
        this.filterServiceNames = new Filter({
          key: 'scrapingServiceName',
          label: 'Órgão autuador',
          table: scrapingServices.map(s => [s.friendlyName, s.name])
        })
      },
      fetchScrapingServices() {
        const params = {
          meta: false,
          per_page: 10000,
          query: {
            enable: true,
            app_name: 'cm-taxas'
          },
          order: ['name', 'asc']
        }

        return this.axios.get('maestro/scraping_services', { params })
          .then(response => EntityProxy.build(response.data))
          .then(scrapingServices => {
            this.scrapingServices = scrapingServices
            this.buildFilterServiceNames(scrapingServices)
          })
          .catch(e => this.$notify.addError(e))
      },
      buildParams(pagination) {
        if (this.query.generic)
          this.query.generic = handleSearchable(this.query.generic)

        const params = ModelMapper.methods.buildParams.call(this, pagination)
        if (params.q?.status && Array.isArray(params.q.status) && params.q.status.includes('processing') && !params.q.status.includes('error'))
          params.q.status = [...params.q.status, 'error']
        return params
      },
      find() {
        this.asyncTemplateSelect()
      },
      translateStatus(value) {
        return {
          processed: 'Processado',
          processing: 'Processando',
          error: 'Processando',
          processed_with_user_error: 'Erro do usuário',
        }[value]
      },
      getServiceFriendlyName(serviceName) {
        const service = this.scrapingServices.find(s => s.name === serviceName)
        return service ? service.friendlyName : serviceName
      }
		},
		mixins: [ModelMapper],
    name: 'Consult',
  }
</script>

<style scoped>
  .btn {
    min-width: 113px;
  }
  
  strong {
    color:#465059;
    font-size: 14px;
  }
</style>
